// Entity shapes mirror docs/PLAN-2A-KEITARO-API-COMPAT.md §2 exactly (field names/order/types).
// Money fields are numbers. Nullable fields are always present (null or "").

export type EntityState = 'active' | 'disabled' | 'deleted'

export interface SourceParameterMap {
  [field: string]: { name?: string; placeholder?: string; alias?: string }
}

export interface CampaignPostback {
  id?: number
  campaign_id?: number
  method: 'GET' | 'POST'
  url: string
  statuses: string[]
}

export interface Campaign {
  id: number
  alias: string
  name: string
  type: 'position' | 'weight'
  uniqueness_method: string
  cookies_ttl: number
  position: number
  state: EntityState
  updated_at: string
  cost_type: 'CPC' | 'CPUC' | 'CPM' | 'CPA' | 'CPS' | 'CPUV' | 'CPV' | 'RevShare'
  cost_value: number
  cost_currency: string
  group_id: number | null
  bind_visitors: string | null
  traffic_source_id: number | null
  token: string
  cost_auto: boolean
  domain_id: number | null
  notes: string
  parameters: SourceParameterMap | [] | null
  uniqueness_use_cookies: boolean
  traffic_loss: number
  bypass_cache: boolean
  created_at: string
  domain: string
  postbacks: CampaignPostback[]
  group: string
  streams?: Stream[]
}

export interface StreamFilterItem {
  id?: number
  stream_id?: number
  name: string
  mode: 'accept' | 'reject'
  payload: string[] | null
  oid?: number
}

export interface StreamLanding {
  id?: number
  stream_id?: number
  landing_id: number
  state?: EntityState
  share: number
  created_at?: string
  updated_at?: string
}

export interface StreamOffer {
  id?: number
  stream_id?: number
  offer_id: number
  state?: EntityState
  share: number
  created_at?: string
  updated_at?: string
}

export interface Stream {
  id: number
  type: 'forced' | 'regular' | 'default'
  name: string
  campaign_id: number
  position: number
  action_options: Record<string, unknown> | [] | null
  comments: string | null
  state: EntityState
  action_type: string
  action_payload: string
  schema: 'landings' | 'redirect' | 'action'
  collect_clicks: boolean
  filter_or: boolean
  weight: number
  offer_selection: 'before_click' | 'after_click'
  filters: StreamFilterItem[]
  triggers: unknown[]
  landings: StreamLanding[]
  offers: StreamOffer[]
}

export interface Landing {
  id: number
  name: string
  action_payload: string
  group_id: number | null
  offer_count: number
  state: 'active' | 'deleted'
  created_at: string
  updated_at: string
  landing_type: 'local' | 'external' | 'preloaded' | 'action'
  notes: string
  action_options: { folder?: string } | Record<string, never>
  action_type: string
  local_path: string
  preview_path: string
  group?: string
}

export interface Offer {
  id: number
  name: string
  group_id: number | null
  action_payload: string | null
  affiliate_network_id: number
  payout_value: number
  payout_currency: string
  payout_type: 'CPA' | 'CPC' | string
  state: EntityState
  created_at: string
  updated_at: string
  payout_auto: boolean
  payout_upsell: boolean
  country: string[]
  notes: string
  action_options: { folder?: string } | []
  action_type: string
  offer_type: 'local' | 'external' | 'preloaded' | 'action'
  conversion_cap_enabled: boolean
  daily_cap: number
  conversion_timezone: string
  alternative_offer_id: number
  values: string | null
  local_path?: string
  preview_path?: string
  group?: string
  affiliate_network?: string
}

export interface Domain {
  id: number
  name: string
  is_ssl: boolean
  network_status: 'validating' | 'active' | 'error'
  default_campaign_id: number
  state: EntityState
  created_at: string
  updated_at: string
  catch_not_found: boolean
  notes: string
  error_description: string | null
  ssl_status: 'issued' | 'error' | null
  ssl_data: null
  next_check_at: string | null
  ssl_redirect: boolean
  allow_indexing: boolean
  check_retries: number
  group_id: number
  admin_dashboard: boolean
  registrar: string | null
  external_id: string | null
  cloudflare_proxy: boolean
  cloudflare_id: string | null
  dns_provider: string
  campaigns_count: string | number
  default_campaign: string
  group: string
  error_solution: string | []
  status: 'active' | 'awaiting_dns'
}

export interface Group {
  id: number
  name: string
  position: number
  type: 'campaigns' | 'offers' | 'landings' | 'domains' | 'affiliate_networks' | 'traffic_sources'
  count?: number
}

export interface TrafficSourceParam {
  param: string
  macro: string
  field: string
  alias: string
}

export interface TrafficSource {
  id: number
  name: string
  postback_url: string
  postback_statuses: string[]
  template_name: string | null
  accept_parameters: boolean
  parameters: SourceParameterMap
  options: null
  state: EntityState
  created_at: string
  updated_at: string
  notes: string
  traffic_loss: number
}

export type AccessType =
  'full_access' | 'read_only' | 'to_groups_and_selected' | 'created_by_user_groups_and_selected'

export interface UserAccessData {
  resources: string[]
  allowed_resources: string[]
  reports: string[]
  [key: `${string}_access_type`]: AccessType | undefined
  [key: `${string}_selected_groups`]: number[] | undefined
  [key: `${string}_selected_entities`]: string[] | undefined
}

/** Keitaro stores `access_data` as an object, but the legacy bootstrap ADMIN carries `[]`
 * (fixture 042) and an API client may send a partial object. Always read it through this so a
 * page never dereferences `.resources` on an array. */
export function normalizeAccessData(raw: unknown): UserAccessData {
  const src =
    raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {}
  const list = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : [])
  return {
    ...(src as Partial<UserAccessData>),
    resources: list(src.resources),
    allowed_resources: list(src.allowed_resources),
    reports: list(src.reports),
  }
}

export interface UserPreferences {
  language?: string
  timezone?: string
  first_weekday?: string
  theme?: string
  [k: string]: unknown
}

export interface User {
  id: number
  login: string
  type: 'ADMIN' | 'USER'
  rules: unknown[]
  permissions: null
  state: EntityState
  /** Object for API-created users; `[]` for the legacy bootstrap ADMIN — read via normalizeAccessData. */
  access_data: UserAccessData | unknown[]
  key_count: number
  keyCount: number
  preferences: UserPreferences
  must_change_password?: boolean
  last_login_at?: string | null
}

export interface AffiliateNetwork {
  id: number
  name: string
  postback_url: string
  offer_param: string
  state: EntityState
  template_name: string | null
  created_at: string
  updated_at: string
  notes: string
}

// ---- Reports ----
export interface ReportRange {
  from?: string
  to?: string
  interval?: string
  timezone?: string
}

export interface ReportFilter {
  name: string
  operator: FilterOperator
  expression?: unknown
  case_sensitive?: boolean
}

export type FilterOperator =
  | 'EQUALS'
  | 'NOT_EQUAL'
  | 'GREATER_THAN'
  | 'LESS_THAN'
  | 'EQUALS_OR_GREATER_THAN'
  | 'EQUALS_OR_LESS_THAN'
  | 'BETWEEN'
  | 'CONTAINS'
  | 'NOT_CONTAIN'
  | 'BEGINS_WITH'
  | 'ENDS_WITH'
  | 'MATCH_REGEXP'
  | 'NOT_MATCH_REGEXP'
  | 'IN_LIST'
  | 'NOT_IN_LIST'
  | 'IS_SET'
  | 'IS_NOT_SET'
  | 'IS_TRUE'
  | 'IS_FALSE'
  | 'IP_BEGINS_WITH'
  | 'HAS_LABEL'
  | 'HAS_NOT_LABEL'
  | 'EXPRESSION'

export interface ReportSort {
  name: string
  order: 'asc' | 'desc' | 'ASC' | 'DESC'
}

export interface ReportRequest {
  range?: ReportRange
  grouping?: string[]
  metrics?: string[]
  columns?: string[]
  filters?: ReportFilter[]
  sort?: ReportSort[]
  summary?: boolean
  limit?: number
  offset?: number
}

export interface ReportRow {
  [key: string]: string | number | boolean | null
}

export interface ReportResponse {
  rows: ReportRow[]
  summary?: ReportRow
  total: number
  meta: { body: number; summary?: number; count: number }
}

export interface LogRequest {
  range?: ReportRange
  limit?: number
  offset?: number
  columns?: string[]
  filters?: ReportFilter[]
  sort?: ReportSort[]
}
